import type {
  BoqRow,
  BuildReportDataInput,
  MatchResult,
  ReportDetailRow,
  ReportExcludedRow,
  ReportParameterRow,
  ReportWorkbookData,
  TotalPriceSource,
} from '../../types'
import { finalizeDiffResults, resolveTolerances, summarizeDiffResults } from '../diff'
import { EXCLUSION_REASON_LABELS } from '../parsing'

export const DEFAULT_REPORT_DISCLAIMER =
  '本工具仅用于机械性差异识别和复核辅助，结果不能替代注册造价工程师或项目造价人员的专业判断。'

function sourceOrMissing(row: BoqRow | null): TotalPriceSource {
  return row?.totalPriceSource ?? 'missing'
}

function detailNote(result: MatchResult): string {
  const notes: string[] = []
  if (
    result.fieldChanges.itemCode.changed &&
    result.baselineRow !== null &&
    result.targetRow !== null
  ) {
    notes.push('项目编码变化')
  }
  if (result.baselineRow?.totalPriceSource === 'calculated') notes.push('基准版合价为计算值')
  if (result.targetRow?.totalPriceSource === 'calculated') notes.push('对比版合价为计算值')
  if (result.amountImpact.state === 'unavailable') notes.push('金额影响无法计算')
  if (result.status === 'pending') notes.push('待人工确认，不计入已确认净影响')
  return notes.join('；')
}

function toDetailRow(result: MatchResult): ReportDetailRow {
  return {
    id: result.id,
    status: result.status,
    matchType: result.matchType,
    confidence: result.confidence,
    matchReason: result.matchReason,
    confirmationState: result.confirmationState,
    baselineSourceRowNumber: result.baselineRow?.sourceRowNumber ?? null,
    targetSourceRowNumber: result.targetRow?.sourceRowNumber ?? null,
    baselineItemCode: result.baselineRow?.itemCode ?? '',
    targetItemCode: result.targetRow?.itemCode ?? '',
    baselineItemName: result.baselineRow?.itemName ?? '',
    targetItemName: result.targetRow?.itemName ?? '',
    baselineItemDescription: result.baselineRow?.itemDescription ?? '',
    targetItemDescription: result.targetRow?.itemDescription ?? '',
    baselineUnit: result.baselineRow?.unit ?? '',
    targetUnit: result.targetRow?.unit ?? '',
    baselineQuantity: result.baselineRow?.quantity ?? null,
    targetQuantity: result.targetRow?.quantity ?? null,
    quantityChange: result.fieldChanges.quantity.absoluteChange,
    quantityPercentageChange: result.fieldChanges.quantity.percentageChange,
    baselineUnitPrice: result.baselineRow?.unitPrice ?? null,
    targetUnitPrice: result.targetRow?.unitPrice ?? null,
    unitPriceChange: result.fieldChanges.unitPrice.absoluteChange,
    unitPricePercentageChange: result.fieldChanges.unitPrice.percentageChange,
    baselineTotalPrice: result.baselineRow?.effectiveTotalPrice ?? null,
    targetTotalPrice: result.targetRow?.effectiveTotalPrice ?? null,
    baselineTotalPriceSource: sourceOrMissing(result.baselineRow),
    targetTotalPriceSource: sourceOrMissing(result.targetRow),
    totalPriceChange: result.fieldChanges.totalPrice.absoluteChange,
    totalPricePercentageChange: result.fieldChanges.totalPrice.percentageChange,
    amountImpact: result.amountImpact.value,
    manuallyConfirmed: result.manualDecision !== null,
    note: detailNote(result),
  }
}

function toExcludedRow(row: BoqRow): ReportExcludedRow {
  return {
    sourceType: row.sourceType,
    fileName: row.fileName,
    sheetName: row.sheetName,
    sourceRowNumber: row.sourceRowNumber,
    rowType: row.rowType,
    exclusionReason: row.exclusionReason ?? 'non-item-row',
    itemCode: row.itemCode,
    itemName: row.itemName,
    itemDescription: row.itemDescription,
    unit: row.unit,
    rawCells: row.rawCells,
  }
}

function comparedAtIso(value: Date | string | undefined): string {
  if (value instanceof Date) return value.toISOString()
  return value ?? new Date().toISOString()
}

function mappingParameter(
  prefix: string,
  mapping: BuildReportDataInput['baseline']['columnMapping'],
): ReportParameterRow {
  return {
    key: `${prefix}ColumnMapping`,
    label: `${prefix === 'baseline' ? '基准版' : '对比版'}字段映射`,
    value: JSON.stringify(mapping),
  }
}

export function buildReportData(input: BuildReportDataInput): ReportWorkbookData {
  const tolerances = resolveTolerances(input.tolerances)
  const finalizedResults = finalizeDiffResults(input.results, tolerances)
  const summary = summarizeDiffResults(finalizedResults)
  const detailRows = finalizedResults.map(toDetailRow)
  const excludedRows = (input.excludedRows ?? [])
    .filter((row) => row.rowType !== 'item' || row.exclusionReason !== null)
    .sort(
      (left, right) =>
        left.sourceType.localeCompare(right.sourceType) ||
        left.sourceRowNumber - right.sourceRowNumber ||
        left.id.localeCompare(right.id),
    )
    .map(toExcludedRow)
  const parameters: ReportParameterRow[] = [
    mappingParameter('baseline', input.baseline.columnMapping),
    mappingParameter('target', input.target.columnMapping),
    { key: 'quantityTolerance', label: '工程量容差', value: tolerances.quantity },
    { key: 'unitPriceTolerance', label: '综合单价容差', value: tolerances.unitPrice },
    { key: 'totalPriceTolerance', label: '合价容差', value: tolerances.totalPrice },
    {
      key: 'highConfidenceThreshold',
      label: '高可信匹配阈值',
      value: input.matchingOptions?.highConfidenceThreshold ?? 0.88,
    },
    {
      key: 'pendingThreshold',
      label: '待确认匹配阈值',
      value: input.matchingOptions?.pendingThreshold ?? 0.72,
    },
    {
      key: 'ambiguityMargin',
      label: '相近候选分差',
      value: input.matchingOptions?.ambiguityMargin ?? 0.03,
    },
    {
      key: 'maxCandidates',
      label: '每行最大候选计算数',
      value: input.matchingOptions?.maxCandidates ?? 50,
    },
    { key: 'localOnly', label: '文件处理方式', value: '仅浏览器本地内存处理' },
  ]

  return {
    summary: {
      baseline: {
        ...input.baseline,
        itemCount: summary.baselineItemCount,
        effectiveTotal: summary.baselineTotal,
        missingTotalCount: summary.baselineMissingTotalCount,
      },
      target: {
        ...input.target,
        itemCount: summary.targetItemCount,
        effectiveTotal: summary.targetTotal,
        missingTotalCount: summary.targetMissingTotalCount,
      },
      comparedAt: comparedAtIso(input.comparedAt),
      countsAndAmounts: summary,
      disclaimer: input.disclaimer ?? DEFAULT_REPORT_DISCLAIMER,
    },
    detailRows,
    addedRows: detailRows.filter((row) => row.status === 'added'),
    removedRows: detailRows.filter((row) => row.status === 'removed'),
    changedRows: detailRows.filter((row) => row.status === 'changed'),
    pendingRows: detailRows.filter((row) => row.status === 'pending'),
    excludedRows,
    parameters,
  }
}

export function exclusionReasonLabel(reason: ReportExcludedRow['exclusionReason']): string {
  return EXCLUSION_REASON_LABELS[reason]
}

export const generateReportData = buildReportData
